// Shared accessible select (v1.3 ticket 05, RCMC-02). A native <select> with a persistent visible label
// replaces the PrimeReact Dropdown (owner ruling Q12) for Company, Request Type and rows per page.
// PrimeReact 10.6.2's Dropdown put the label's id on a wrapper div, so its focusable field was unnamed and
// exposed the option's internal value (a company's database id) instead of the name on screen. A native
// select is announced as a combobox with its label and the chosen option's text, and the browser supplies
// the arrow-key behaviour screen readers expect. It has no overlay, so nothing renders outside a dialog.
//
//   <Select id="add-holding-company" label="Company" placeholder="Select a company"
//           options={[{ label: "Demo Industries Ltd", value: 37 }]} value={companyId} onChange={setCompanyId} />
//
// Option text is the visible name; the caller's ids are only option values. onChange hands back the chosen
// option's own value (a number stays a number), so request payloads are unchanged.
//
// Required (ticket 06, RCMC-18) is `aria-required`, not the native attribute: a native `required` makes an
// empty select fail constraint validation (:invalid, checkValidity) before the Investor has pressed anything,
// while the forms here report their own errors on submit. The visible "*" is hidden from screen readers, so
// the accessible name stays the bare label and "required" is said once. Here it sits after the <label>, not
// inside it, so the label's own text is the bare name too (what getByLabelText and voice control match).
//
// Inline (ticket 13, RCMC-10): the label sits beside the control instead of above it, for a table's toolbar ("Rows per
// page"); the control is as wide as its options.
//
// A choice cut short (ticket 16, RCMC-04 / RCMC-21): a closed select shows its choice on one line, cut with an ellipsis
// when the control is too narrow — at 320px, "Change/updation of bank details/ECS mandate/Dividend" and the other long
// Request Types and company names. Then the whole choice is also shown below the control, so it reads in full. A screen
// reader already reads the whole choice from the select, so the copy is hidden from it (not read twice). Where the
// choice fits, nothing is added. At every width (ticket 22, owner ruling Q37; 16 hid it at 1280px and up): a long
// company name is cut short in Add New Holding's 25vw select at 1280 too.
//
// Investor screens use this. CustomDropdown.jsx (PrimeReact) stays on the Operations-only screens, which this
// remediation does not cover (SPEC "Scope boundary").
import { useEffect, useRef, useState } from "react";
import { classNames } from "primereact/utils";
import styles from "./Select.module.css";

// Measures text in a given font (one canvas, reused)
let measuringCanvas;
/** How many CSS px `text` takes in `select`'s font, or 0 when the browser cannot say (no layout, no canvas) */
function textWidth(text, select) {
  measuringCanvas = measuringCanvas || document.createElement("canvas");
  const context = measuringCanvas.getContext("2d");
  if (!context) return 0;
  const style = window.getComputedStyle(select);
  context.font = style.font || `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  return context.measureText(text).width;
}

/** True when `select` is too narrow to show `text` on its line (its width less its padding: the chevron's room) */
function cutsShort(select, text) {
  if (!select.clientWidth) return false; // not laid out (hidden, or jsdom): nothing is shown, so nothing is cut
  const style = window.getComputedStyle(select);
  const room = select.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
  return textWidth(text, select) > room;
}

/**
 * @param {object} props
 * @param {string} props.id            the select's id; the label points at it, the hint/error ids derive from it
 * @param {string} props.label         visible, persistent label; also the accessible name
 * @param {{label: string, value: string|number}[]} props.options  option text and the value it stands for
 * @param {string|number} props.value  the chosen option's value; "" = nothing chosen yet
 * @param {(value: string|number) => void} props.onChange  gets the option's own value, or "" for the placeholder
 * @param {string} [props.placeholder] first option (value ""), shown while nothing is chosen. Without one,
 *                                     `value` must always match an option, or the browser shows the first.
 * @param {boolean} [props.required]  aria-required + a visible "*" after the label (the form shows the key)
 * @param {string} [props.hint]        help text, linked by aria-describedby
 * @param {string} [props.error]       error text: linked by aria-describedby and marks the select invalid
 * @param {string} [props.className]   class for the outer wrapper (its spacing in the caller's layout)
 * @param {boolean} [props.inline]     label beside the control, control as wide as its options (a toolbar)
 * Any other prop (name, disabled, …) goes to the <select>.
 */
const Select = ({ id, label, options, value, onChange, placeholder, required = false, hint, error, className, inline = false, ...props }) => {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined; // no empty attribute

  // Ticket 16: the chosen option's text, and whether the closed select cuts it short. Re-checked when the choice
  // changes and whenever the select changes width (a zoom, a resize, a dialog opening)
  const selectRef = useRef(null);
  const chosen = options.find((option) => String(option.value) === String(value ?? ""))?.label;
  const [cutShort, setCutShort] = useState(false);
  useEffect(() => {
    const select = selectRef.current;
    if (!select || !chosen || inline) {
      setCutShort(false);
      return undefined;
    }
    const update = () => setCutShort(cutsShort(select, chosen));
    update();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(update) : null;
    observer?.observe(select);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [chosen, inline]);

  // The DOM only knows strings; find the option so the caller gets back the value it supplied
  const handleChange = (event) => {
    const chosen = options.find((option) => String(option.value) === event.target.value);
    onChange(chosen ? chosen.value : "");
  };

  return (
    <div className={classNames(styles.field, { [styles.inline]: inline }, className)}>
      <label htmlFor={id} className={styles.label}>{label}</label>
      {required && <span className={styles.required} aria-hidden="true">*</span>}
      {hint && <div id={hintId} className={styles.hint}>{hint}</div>}
      <div className={styles.control}>
        {/* p-inputtext: the same border, padding, font and focus style as the PrimeReact text fields beside it */}
        <select
          {...props}
          ref={selectRef}
          id={id}
          value={value ?? ""}
          onChange={handleChange}
          aria-required={required ? "true" : undefined}
          aria-invalid={error ? "true" : undefined}
          aria-describedby={describedBy}
          className={classNames("p-inputtext p-component", { "p-invalid": error }, styles.select)}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      {cutShort && <div className={styles.choice} aria-hidden="true">{chosen}</div>}
      {error && <div id={errorId} className={styles.error}>{error}</div>}
    </div>
  );
};

export default Select;
